import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ServicePageTemplate = ({ service }) => {
  const [openFaq, setOpenFaq] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fallback = setTimeout(() => setIsVisible(true), 120);
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { clearTimeout(fallback); setIsVisible(true); } },
      { threshold: 0.05 }
    );
    if (contentRef.current) observer.observe(contentRef.current);
    return () => {
      clearTimeout(fallback);
      if (contentRef.current) observer.unobserve(contentRef.current);
    };
  }, []);

  return (
    <>
      <title>{service.metaTitle}</title>
      <meta name="description" content={service.metaDescription} />
      <Header />

      {/* Hero Banner */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Link to="/services" className="inline-flex items-center gap-2 text-white/60 hover:text-white/90 text-sm mb-6 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              All Services
            </Link>
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white border border-white/20">
                {service.icon}
              </div>
            </div>
            <span className="inline-block text-white/70 text-xs font-semibold uppercase tracking-widest mb-4 bg-white/10 px-4 py-1.5 rounded-full">
              {service.category}
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {service.bannerTitle}
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              {service.bannerSubtitle}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact-us"
                className="bg-white text-slate-900 px-8 py-4 rounded-full font-semibold text-sm hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                Get Started Today
              </Link>
              <a
                href="#overview"
                className="border-2 border-white/40 text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-white/10 transition-all duration-300"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-10 bg-white border-b border-slate-100 shadow-sm">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {service.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold gradient-text font-display mb-1">{stat.value}</div>
                <div className="text-slate-500 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div ref={contentRef}>
        {/* Overview */}
        <section id="overview" className="section-padding bg-slate-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className={`space-y-6 ${isVisible ? 'animate-slideInLeft' : 'opacity-0'}`}>
                <div>
                  <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Overview</span>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
                    {service.overview.title}
                  </h2>
                  <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mb-6"></div>
                </div>
                <p className="text-slate-600 text-lg leading-relaxed">{service.overview.description}</p>
                <ul className="space-y-3">
                  {service.overview.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {h}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact-us"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  Discuss Your Project
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              <div className={`${isVisible ? 'animate-slideInRight' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                <div className={`rounded-3xl p-8 bg-gradient-to-br ${service.bannerGradient} text-white`}>
                  <h3 className="font-display text-xl font-bold mb-6">What We Deliver</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {service.deliverables.map((d, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
                        <svg className="w-4 h-4 text-white/70 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-sm font-medium">{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className={`text-center mb-14 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Why Choose Us</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Key Benefits</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {service.benefits.map((benefit, i) => (
                <div
                  key={i}
                  className={`bg-slate-50 rounded-2xl p-7 card-hover ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${service.bannerGradient} rounded-xl flex items-center justify-center text-white mb-5`}>
                    {benefit.icon}
                  </div>
                  <h3 className="font-bold text-lg text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <div className={`text-center mb-14 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">How We Work</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Our Process</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mb-4"></div>
              <p className="text-slate-600 max-w-xl mx-auto">A proven, structured approach that ensures on-time delivery and exceptional quality every step of the way.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
              {service.process.map((step, i) => (
                <div
                  key={i}
                  className={`relative text-center group ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.bannerGradient} rounded-2xl flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    {step.step}
                  </div>
                  <h4 className="font-bold text-slate-900 text-base mb-2">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className={`text-center mb-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Tech Stack</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Technologies We Use</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto"></div>
            </div>
            <div className={`flex flex-wrap justify-center gap-4 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              {service.technologies.map((tech, i) => (
                <span
                  key={i}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 rounded-xl text-sm font-semibold transition-all duration-200 border border-slate-200 hover:border-blue-200 cursor-default"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="container-custom text-center text-white">
            <div className={`max-w-3xl mx-auto ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{service.cta.title}</h2>
              <p className="text-gray-300 text-lg mb-10 leading-relaxed">{service.cta.subtitle}</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact-us"
                  className={`bg-gradient-to-r ${service.bannerGradient} text-white px-10 py-4 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-2xl`}
                >
                  Start Your Project
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-white/30 text-white px-10 py-4 rounded-full font-semibold text-sm hover:bg-white/10 transition-all duration-300"
                >
                  Explore Other Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className={`text-center mb-14 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">FAQs</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Frequently Asked Questions</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto"></div>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {service.faqs.map((faq, i) => (
                <div
                  key={i}
                  className={`border border-slate-200 rounded-2xl overflow-hidden ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                  style={{ animationDelay: `${i * 0.07}s` }}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-50 transition-colors duration-200"
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-blue-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`transition-all duration-300 ease-in-out overflow-hidden ${openFaq === i ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className={`text-center mt-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
              <p className="text-slate-600 mb-4">Still have questions? We're here to help.</p>
              <Link
                to="/contact-us"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors"
              >
                Contact our team
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default ServicePageTemplate;
